import type { PlanData, PlanDay } from '@/features/ai-plan/lib/types'
import type { CheckedMap, DayPosition, PlanChangedDetail, TrainingProgressDetail } from './trainingBridge'
import { computeDayProgress, dayDoneKey, exerciseKey, phaseKey, type PhaseName } from '@/features/training/lib/dayProgress'

const ACTIVE_PLAN_KEY = 'af_pt_active_plan'
const CHECKED_KEY = 'af_pt_plan_checked'
const DAY_ONE: DayPosition = { week: 1, dayOfWeek: 1 }

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

function syncActivePlan(plan: PlanData): void {
  const user = window.authService?.currentUser
  if (!user || !window.syncService) return
  window.syncService.syncPlan(user.uid, plan).catch(() => {
    // Existing app sync is best-effort; keep Training shell responsive.
  })
}

export function emitPlanChanged(detail: PlanChangedDetail): void {
  window.dispatchEvent(new CustomEvent<PlanChangedDetail>('ptr:plan-changed', { detail }))
}

export function getActivePlan(): PlanData | null {
  return readJson<PlanData | null>(ACTIVE_PLAN_KEY, null)
}

export function getPosition(): DayPosition {
  return { ...DAY_ONE }
}

export function getDay(plan: PlanData | null = getActivePlan(), pos: DayPosition = DAY_ONE): PlanDay | null {
  if (!plan?.week_template?.length) return null
  const template = pos.week > Math.ceil((plan.total_weeks || 1) / 2) && Array.isArray(plan.week_template_phase2)
    ? plan.week_template_phase2
    : plan.week_template
  return template.find(day => day.day_of_week === pos.dayOfWeek) ?? template[0] ?? null
}

export function setActivePlan(plan: PlanData, source: Extract<PlanChangedDetail['source'], 'generate' | 'import'>): void {
  localStorage.setItem(ACTIVE_PLAN_KEY, JSON.stringify(plan))
  localStorage.removeItem(CHECKED_KEY)
  window.setPlanData?.(plan)
  syncActivePlan(plan)
  emitPlanChanged({ source })
}

export function clearActivePlan(): void {
  localStorage.removeItem(ACTIVE_PLAN_KEY)
  localStorage.removeItem(CHECKED_KEY)
  // Phase 3: We call setPlanData(null) to keep vanilla's in-memory state
  // (currentPlanData) from becoming stale after we clear through the bridge.
  // This is a temporary measure. Once the old plan surfaces are fully retired,
  // we can remove this call.
  window.setPlanData?.(null)
  emitPlanChanged({ source: 'clear' })
}

// ---------------------------------------------------------------------------
// Completion (Phase 4) — read/write af_pt_plan_checked + emit progress.
// Mutations return the updated map so callers can refresh reactive state.
// ---------------------------------------------------------------------------

export function getChecked(): CheckedMap {
  return readJson<CheckedMap>(CHECKED_KEY, {})
}

/** Best-effort Firestore sync of completion, mirroring legacy app.js. */
export function syncPlanChecked(checked: CheckedMap): void {
  const user = window.authService?.currentUser
  if (!user || !window.syncService?.syncPlanChecked) return
  window.syncService.syncPlanChecked(user.uid, checked).catch(() => {
    // best-effort
  })
}

function persistChecked(checked: CheckedMap): void {
  try {
    localStorage.setItem(CHECKED_KEY, JSON.stringify(checked))
  } catch {
    // quota / unavailable — keep UI responsive
  }
  syncPlanChecked(checked)
}

export function emitTrainingProgress(detail: TrainingProgressDetail): void {
  window.dispatchEvent(new CustomEvent<TrainingProgressDetail>('ptr:training-progress-changed', { detail }))
}

function emitProgressFor(pos: DayPosition, checked: CheckedMap): void {
  const { pct } = computeDayProgress(getDay(getActivePlan(), pos), pos, checked)
  emitTrainingProgress({ weekIndex: pos.week, completePct: pct })
}

export function getDayProgressPct(pos: DayPosition = DAY_ONE): number {
  return computeDayProgress(getDay(getActivePlan(), pos), pos, getChecked()).pct
}

export function isDayDone(pos: DayPosition): boolean {
  return getChecked()[dayDoneKey(pos)] === true
}

export function toggleExercise(pos: DayPosition, exerciseIndex: number): CheckedMap {
  const checked = getChecked()
  const key = exerciseKey(pos, exerciseIndex)
  checked[key] = !checked[key]
  persistChecked(checked)
  emitProgressFor(pos, checked)
  return checked
}

export function markPhaseDone(pos: DayPosition, phase: PhaseName): CheckedMap {
  const checked = getChecked()
  const key = phaseKey(pos, phase)
  checked[key] = !checked[key]
  persistChecked(checked)
  emitProgressFor(pos, checked)
  return checked
}

export function markDayDone(pos: DayPosition): CheckedMap {
  const checked = getChecked()
  const done = !checked[dayDoneKey(pos)]
  checked[dayDoneKey(pos)] = done
  // Mirror legacy markDayDone: completing a day checks all of its units.
  const day = getDay(getActivePlan(), pos)
  if (day && !day.is_rest) {
    const count = day.exercises?.length ?? 0
    for (let i = 0; i < count; i++) checked[exerciseKey(pos, i)] = done
    if ((day.warmup?.length ?? 0) > 0) checked[phaseKey(pos, 'warmup')] = done
    if ((day.cooldown?.length ?? 0) > 0) checked[phaseKey(pos, 'cooldown')] = done
  }
  persistChecked(checked)
  emitProgressFor(pos, checked)
  return checked
}

/** Reuse the legacy vanilla rest timer (timer-modal) via the bridge. */
export function startRestTimer(seconds: number, label = 'Rest', sets?: number): void {
  window.startTimer?.(seconds, label, sets)
}
